// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// See LICENSE.md for licensing terms.

import type { TourAction } from '@vn-grc/shared';

export function isVisibleTourElement(el: Element): boolean {
  if (el.closest('[hidden], [aria-hidden="true"]')) return false;
  for (let node: Element | null = el; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}

/**
 * Sprint 63.44.0 (D7) — the attributes driver.js writes on every highlighted
 * element (`aria-haspopup="dialog"`, `aria-expanded="true"`, `aria-controls`)
 * and removes outright on unhighlight. On a generic element they are not
 * allowed (axe `aria-allowed-attr`, critical); on a control they overwrite,
 * then delete, values the app set itself.
 */
const DRIVER_POPUP_ARIA = ['aria-haspopup', 'aria-expanded', 'aria-controls'] as const;

const attributeValues = (el: Element): (string | null)[] =>
  DRIVER_POPUP_ARIA.map((name) => el.getAttribute(name));

function writeValues(el: Element, values: readonly (string | null)[]): void {
  DRIVER_POPUP_ARIA.forEach((name, i) => {
    const value = values[i];
    if (value === null) el.removeAttribute(name);
    else el.setAttribute(name, value);
  });
}

/**
 * Sprint 63.44.0 (D7; gate E12.5 passes 1 and 2) — keeps the app's own popup
 * ARIA on every element the tour has highlighted.
 *
 * driver.js removes its attributes from its `__activeElement`, which it
 * updates only when a highlight's transition (~400 ms) ends: Next pressed
 * mid-animation strips an element two steps back. So every element the tour
 * highlighted is tracked (not only the last), and after each highlight every
 * one that is not the current target gets the app's values back (a control
 * being highlighted keeps driver's while it is).
 *
 * The app's values are recorded when an element is first tracked and then
 * followed with a MutationObserver: a write to one of the three attributes
 * outside the guard's own window (`highlight`, `destroy`) is the app's and
 * replaces the record, whatever its value — including one equal to what
 * driver wrote (a step's click opening a menu sets `aria-expanded="true"`).
 * Writes inside the window (driver's and the guard's own) are taken from the
 * observer and discarded. Removed elements are dropped.
 */
export interface DriverAriaGuard {
  /** Run `driver.highlight` for `target` (`null` when no element matched). */
  highlight(target: Element | null, run: () => void): void;
  /** Run `driver.destroy`, give every tracked element its values back, and stop. */
  destroy(run: () => void): void;
}

export function createDriverAriaGuard(): DriverAriaGuard {
  const tracked = new Map<Element, (string | null)[]>();
  const names: readonly string[] = DRIVER_POPUP_ARIA;
  const record = (mutations: MutationRecord[]) => {
    for (const m of mutations) {
      const values = tracked.get(m.target as Element);
      const i = m.attributeName ? names.indexOf(m.attributeName) : -1;
      if (values && i >= 0) values[i] = (m.target as Element).getAttribute(names[i]);
    }
  };
  const observer = typeof MutationObserver === 'undefined' ? null : new MutationObserver(record);
  /** Apply the app's writes not yet delivered, then run `fn` with its own writes discarded. */
  const inWindow = (fn: () => void) => {
    if (observer) record(observer.takeRecords());
    for (const el of tracked.keys()) if (!el.isConnected) tracked.delete(el);
    try {
      fn();
    } finally {
      observer?.takeRecords();
    }
  };
  const restoreAllBut = (keep: Element | null) => {
    for (const [el, values] of tracked)
      if (el !== keep || !isPopupCapableControl(el)) writeValues(el, values);
  };
  return {
    highlight(target, run) {
      inWindow(() => {
        if (target && !tracked.has(target)) {
          tracked.set(target, attributeValues(target));
          observer?.observe(target, { attributes: true, attributeFilter: [...names] });
        }
        try {
          run();
        } finally {
          restoreAllBut(target);
        }
      });
    },
    destroy(run) {
      inWindow(() => {
        try {
          run();
        } finally {
          restoreAllBut(null);
        }
      });
      observer?.disconnect();
      tracked.clear();
    },
  };
}

/**
 * An element whose role allows `aria-haspopup` / `aria-expanded` (WAI-ARIA
 * 1.2): native controls and the interactive roles. Anything else is generic
 * content the tour presents, not a control that opens the step's popover.
 */
export function isPopupCapableControl(el: Element): boolean {
  return el.matches(
    'button, a[href], summary, select, textarea, input:not([type="hidden"]), [role="button"], [role="link"], [role="combobox"], [role="menuitem"], [role="tab"], [role="treeitem"], [role="gridcell"], [role="application"]',
  );
}

export function findVisibleTourTarget(
  selector: string,
  blockedText: readonly string[] = [],
): Element | null {
  const el = document.querySelector(selector);
  if (!el || !isVisibleTourElement(el)) return null;
  const page = el.closest('main') ?? el.closest('.dm-page');
  // A heading is not proof of a loaded/authorized page. These are the shipped
  // blocking-state markers; ordinary warning alerts remain valid tour content.
  if (
    page &&
    Array.from(
      page.querySelectorAll(
        '.skeleton-loader, .dm-error-boundary, .dm-module-error, .dm-error, [aria-busy="true"], [data-testid="access-denied"]',
      ),
    ).some(isVisibleTourElement)
  )
    return null;
  if (el.closest('[aria-busy="true"], .skeleton-loader, [data-testid="access-denied"]'))
    return null;
  if (
    page &&
    Array.from(page.querySelectorAll('h1, p')).some(
      (node) => isVisibleTourElement(node) && blockedText.includes(node.textContent?.trim() ?? ''),
    )
  )
    return null;
  return el;
}

export async function waitForTourElement(
  selector: string,
  timeout: number,
  cancelled: () => boolean,
  blockedText: readonly string[] = [],
): Promise<Element | null> {
  const start = Date.now();
  while (!cancelled() && Date.now() - start < timeout) {
    const el = findVisibleTourTarget(selector, blockedText);
    if (el) return el;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  return null;
}

/** Every DOM write rechecks the run token after waiting for its target. */
export async function executeTourAction(
  action: TourAction,
  navigate: (path: string) => void,
  cancelled: () => boolean,
  timeout: number,
): Promise<void> {
  if (cancelled()) return;
  const findTarget = async (selector: string) => {
    const el = await waitForTourElement(selector, timeout, cancelled);
    if (cancelled()) return null;
    if (!el) throw new Error('Tour action target unavailable');
    return el;
  };
  switch (action.type) {
    case 'click': {
      const el = await findTarget(action.target);
      if (!el) return;
      if (!(el instanceof HTMLElement) || el.matches(':disabled, [aria-disabled="true"]'))
        throw new Error('Tour action control unavailable');
      el.click();
      break;
    }
    case 'fill': {
      const el = await findTarget(action.target);
      if (!el) return;
      if (
        !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) ||
        el.disabled ||
        el.readOnly
      )
        throw new Error('Tour input unavailable');
      el.focus();
      const setter = Object.getOwnPropertyDescriptor(
        el instanceof HTMLTextAreaElement
          ? HTMLTextAreaElement.prototype
          : HTMLInputElement.prototype,
        'value',
      )?.set;
      const delay = Math.min(30, Math.floor(1500 / Math.max(1, action.value.length)));
      for (let i = 0; i <= action.value.length; i++) {
        if (cancelled()) return;
        setter?.call(el, action.value.slice(0, i));
        el.dispatchEvent(new Event('input', { bubbles: true }));
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      break;
    }
    case 'select': {
      const el = await findTarget(action.target);
      if (!el) return;
      if (
        !(el instanceof HTMLSelectElement) ||
        el.disabled ||
        !Array.from(el.options).some((option) => option.value === action.value && !option.disabled)
      )
        throw new Error('Tour select option unavailable');
      Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set?.call(
        el,
        action.value,
      );
      el.dispatchEvent(new Event('change', { bubbles: true }));
      break;
    }
    case 'hover': {
      const el = await findTarget(action.target);
      if (el) el.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
      break;
    }
    case 'wait':
      await new Promise((resolve) => setTimeout(resolve, action.ms));
      break;
    case 'navigate':
      navigate(action.to);
      await new Promise((resolve) => setTimeout(resolve, 500));
      break;
    case 'scroll_to': {
      const el = await findTarget(action.target);
      if (el) el.scrollIntoView({ behavior: 'instant', block: 'center' });
      break;
    }
  }
}
